/**
 * 自由オブジェクトの1クリック設置。
 * ヒット点は呼び出し側で面スナップ済み。ここは姿勢と壁ずらしだけを決める。
 */

import { vec } from './geometry';
import {
  normalizeObjectYaw,
  objectWorldPoint,
  rotateLocalPoint,
  surfaceMountPose,
  type ObjectBounds,
} from './object-pose';
import type { ObjectDefinition, ObjectGeometry, ObjectMountAxis, Vec3 } from './types';
import { localToWorldXZ } from './view-grid';

const WALL_NORMAL_Y = 0.5;
const OVERLAP_EPS = 1e-4;

export interface PlaceObjectInput {
  definition: ObjectDefinition;
  /** 面格子へスナップ済みの設置点。 */
  hit: Vec3;
  /** クリックした面の外側法線（部屋側）。 */
  normal: Vec3;
  /** 視線方向。局所 +Z を合わせる。 */
  look: Vec3;
  cameraY: number;
  /** スナップ格子の yaw（度）。省略時は 0。 */
  snapYaw?: number;
}

/** 体積格子へ飛んだスナップを、照準した面へ戻す。 */
export function onAimedSurface(snapped: Vec3, aim: Vec3, outward: Vec3): Vec3 {
  const n = vec.normalize(outward);
  if (vec.length(n) < 1e-9) return { ...snapped };
  return vec.sub(snapped, vec.scale(n, vec.dot(vec.sub(snapped, aim), n)));
}

export function snapOnSurface(point: Vec3, normal: Vec3, snap: number, yawHint = 0): Vec3 {
  const step = snap > 0 ? snap : 1;
  const n = vec.normalize(normal);
  if (vec.length(n) < 1e-9) return { ...point };
  const hint = localToWorldXZ(1, 0, yawHint);
  let u = vec.sub({ x: hint.x, y: 0, z: hint.z }, vec.scale(n, n.x * hint.x + n.z * hint.z));
  if (vec.length(u) < 1e-6) {
    const helper = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    u = vec.cross(n, helper);
  }
  u = vec.normalize(u);
  const v = vec.normalize(vec.cross(n, u));
  if (vec.length(v) < 1e-9) return { ...point };
  const planeOrigin = vec.scale(n, vec.dot(n, point));
  const rel = vec.sub(point, planeOrigin);
  const uCoord = Math.round(vec.dot(rel, u) / step) * step;
  const vCoord = Math.round(vec.dot(rel, v) / step) * step;
  return vec.add(planeOrigin, vec.add(vec.scale(u, uCoord), vec.scale(v, vCoord)));
}

export function shouldSitUpright(normal: Vec3, cameraY: number, hitY: number): boolean {
  if (normal.y >= WALL_NORMAL_Y) return true;
  if (normal.y <= -WALL_NORMAL_Y) return false;
  return cameraY > hitY;
}

/** 面に乗る直方体の接触点。直立の壁では下端 / 上端を hit.y に残す。 */
function surfaceContact(
  bounds: ObjectBounds,
  yaw: number,
  mount: ObjectMountAxis,
  outward: Vec3,
  sit?: boolean
): Vec3 {
  const mid = {
    x: (bounds.min.x + bounds.max.x) * 0.5,
    y: (bounds.min.y + bounds.max.y) * 0.5,
    z: (bounds.min.z + bounds.max.z) * 0.5,
  };
  const faces = [
    { p: { x: bounds.max.x, y: mid.y, z: mid.z }, n: { x: 1, y: 0, z: 0 } },
    { p: { x: bounds.min.x, y: mid.y, z: mid.z }, n: { x: -1, y: 0, z: 0 } },
    { p: { x: mid.x, y: bounds.max.y, z: mid.z }, n: { x: 0, y: 1, z: 0 } },
    { p: { x: mid.x, y: bounds.min.y, z: mid.z }, n: { x: 0, y: -1, z: 0 } },
    { p: { x: mid.x, y: mid.y, z: bounds.max.z }, n: { x: 0, y: 0, z: 1 } },
    { p: { x: mid.x, y: mid.y, z: bounds.min.z }, n: { x: 0, y: 0, z: -1 } },
  ];
  let best = faces[0];
  let bestDot = -Infinity;
  for (const face of faces) {
    const alignment = -vec.dot(rotateLocalPoint(face.n, yaw, mount), outward);
    if (alignment > bestDot) {
      bestDot = alignment;
      best = face;
    }
  }
  if (sit === undefined) return best.p;
  return { ...best.p, y: sit ? bounds.min.y : bounds.max.y };
}

/** 直方体全体を面の部屋側へ出す。 */
function flushToSurface(
  origin: Vec3,
  bounds: ObjectBounds,
  yaw: number,
  mount: ObjectMountAxis,
  hit: Vec3,
  outward: Vec3
): Vec3 {
  const n = vec.normalize(outward);
  if (vec.length(n) < 1e-9) return origin;
  let minDot = Infinity;
  for (const x of [bounds.min.x, bounds.max.x]) {
    for (const y of [bounds.min.y, bounds.max.y]) {
      for (const z of [bounds.min.z, bounds.max.z]) {
        const world = objectWorldPoint({ x, y, z }, origin, yaw, mount);
        const d = vec.dot(vec.sub(world, hit), n);
        if (d < minDot) minDot = d;
      }
    }
  }
  if (minDot >= -OVERLAP_EPS) return origin;
  return vec.add(origin, vec.scale(n, -minDot));
}

export function placeObject(input: PlaceObjectInput): ObjectGeometry {
  const normal = vec.normalize(input.normal);
  const outward = vec.length(normal) < 1e-9 ? { x: 0, y: 1, z: 0 } : normal;
  const snapYaw = input.snapYaw ?? 0;
  const fixture = input.definition.placement === 'surfaceMount';
  const { yaw, mount } = fixture
    ? surfaceMountPose(outward, snapYaw)
    : { yaw: normalizeObjectYaw(snapYaw), mount: '+y' as const };
  const sit = fixture ? undefined : shouldSitUpright(outward, input.cameraY, input.hit.y);
  const contact = rotateLocalPoint(
    surfaceContact(input.definition.bounds, yaw, mount, outward, sit),
    yaw,
    mount
  );
  const origin = flushToSurface(
    vec.sub(input.hit, contact),
    input.definition.bounds,
    yaw,
    mount,
    input.hit,
    outward
  );
  return fixture ? { origin, yaw, mount } : { origin, yaw };
}
